import React, { useEffect, useMemo, useRef, useState } from 'react'
import Popover from '@mui/material/Popover'
// ProfitBreakdown brings its own stylesheet, popover Paper class included.
import {
    editableProfitRows, ProfitGrid, rowsToProfitPayload, signedRowsTotal, withTrailingBlank,
} from './ProfitBreakdown'

// A closed deal's profit grid, editable from Lead History. Edits stay local
// until Save, which hands `onSave` the profit payload and closes only when it
// reports success. Cancel, Escape and a click away drop them; the next opening
// starts again from the opportunity as it is then.
export const EditableProfitPopover = ({ opportunity, anchorEl, onClose, onSave }) => {
    const open = Boolean(anchorEl && opportunity)
    const stored = useMemo(() => (opportunity ? editableProfitRows(opportunity) : []), [opportunity])
    // Unsaved rows; null until the first edit of this opening.
    const [draft, setDraft] = useState(null)
    const [saving, setSaving] = useState(false)
    // Stops a second click that lands before the disabled button renders.
    const savingRef = useRef(false)
    const rows = draft ?? stored

    // Every way of closing lands here, so none of them carries edits over.
    useEffect(() => {
        if (!open) setDraft(null)
    }, [open])

    const patchRow = (key, patch) => setDraft(
        withTrailingBlank(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    )
    const removeRow = (key) => setDraft(withTrailingBlank(rows.filter((r) => r.key !== key)))

    const save = async () => {
        if (savingRef.current) return
        savingRef.current = true
        setSaving(true)
        let ok = false
        try {
            ok = await onSave(rowsToProfitPayload(rows))
        } catch (e) {
            /* the store reports its own failure; the edits stay */
        }
        savingRef.current = false
        setSaving(false)
        if (ok) onClose()
    }

    // Not mid-save: closing then would drop the edits a failed save keeps.
    const close = () => {
        if (!savingRef.current) onClose()
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={close}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ className: 'profit-breakdown-popover' }}
        >
            <ProfitGrid rows={rows} onPatch={patchRow} onRemove={removeRow} total={signedRowsTotal(rows)} />
            <div className="pb-actions">
                <button type="button" className="pb-cancel" onClick={close} disabled={saving}>Cancel</button>
                <button type="button" className="pb-save" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </Popover>
    )
}
